import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Speaker-button presentation, derived from a CameraTile media state.
 *
 * One module so the two live toolbars (streamCamera, TrackingView) cannot drift
 * apart on the three-state rules, and so those rules are stated once:
 *
 *   'available'   — the device reported a microphone on THIS effective channel.
 *   'unavailable' — the device reported NO microphone on this channel.
 *   'unknown'     — nobody has told us: old firmware, old signaling, or the
 *                   device has not reported yet.
 *
 * 'unknown' is unproven, NOT negative. It must never render "no microphone" and
 * must never be cached as a no — a tile that remembers it stays silent forever
 * once the device starts reporting. The SDK already re-derives capability from
 * every session_bootstrap, so simply not caching it here is the whole job.
 *
 * Capability is keyed on the effective channel (101/102/201/202), so it changes
 * under the user on an HD↔SD toggle. Nothing here memoises across that switch.
 *
 * Playback is never gated on the flag. If capability says unavailable and audio
 * arrives anyway, the SDK plays it — the wire is ground truth and the flag is
 * an advertisement. This module only decides what the button looks like.
 */

/**
 * How long a live tile may advertise audio and deliver none before the UI says
 * so. Deliberately generous: the device gates audio uplink on viewer demand, so
 * a 2 s timeout fires on healthy streams.
 */
export const AUDIO_NO_DATA_GRACE_MS = 5000;

export const AUDIO_LABELS = {
  unmute: 'Bật âm thanh',
  mute: 'Tắt âm thanh',
  noMic: 'Kênh này không có micro',
  noAudio: 'Không nhận được âm thanh từ kênh này',
  // The browser will not play audio in this page context at all.
  //
  // Deliberately cause-NEUTRAL. An insecure origin is the most common cause
  // (AudioWorklet is secure-context only, while AudioContext still constructs
  // and reports 'running', so the only symptom is silence — http://<lan-ip>:3000
  // hits this, http://localhost:3000 and any HTTPS origin do not), but it is not
  // the only one: a CSP without `script-src blob:`, a browser with no Web Audio,
  // or a graph that failed to build all land here too. Naming the insecure
  // origin unconditionally sends someone to check the URL of a page that is
  // already on HTTPS. The SDK's own reason string is appended instead, since it
  // knows which of those actually happened.
  blocked: 'Trình duyệt không phát được âm thanh trên trang này'
};

/** Cap on the SDK reason appended to the tooltip, so it stays a tooltip. */
const REASON_MAX = 180;

/**
 * @param {object} args
 * @param {'available'|'unavailable'|'unknown'} args.audioCapability
 * @param {boolean} args.audioReceiving  whether any 0x04 has arrived
 * @param {string|null} args.audioUnavailableReason  why playback is impossible here
 * @param {boolean} args.muted           read back from the real AudioContext state
 * @param {boolean} args.isLive
 * @param {boolean} args.graceExpired    live for longer than AUDIO_NO_DATA_GRACE_MS
 * @returns {{disabled: boolean, label: string, tone: 'muted'|'unmuted'|'no-mic'|'no-audio'}}
 */
export function deriveAudioAffordance({
  audioCapability,
  audioReceiving,
  audioUnavailableReason,
  muted,
  isLive,
  graceExpired
}) {
  // Checked before capability: this browser cannot play audio from ANY channel,
  // so the microphone question is moot. Reporting "no microphone" here would
  // send someone to the vehicle to check hardware that is working fine.
  if (audioUnavailableReason) {
    const detail = String(audioUnavailableReason).slice(0, REASON_MAX);
    return {
      disabled: true,
      // The Vietnamese sentence is what the operator reads; the SDK's reason is
      // appended for whoever they forward the screenshot to. Keeping both means
      // the message never has to guess at the cause.
      label: `${AUDIO_LABELS.blocked} (${detail})`,
      tone: 'blocked'
    };
  }
  // The device says this channel has no microphone. Disabled rather than
  // hidden: a missing button reads as a broken UI, a disabled one with a reason
  // reads as an answer. Layout also stays stable across an HD↔SD toggle that
  // changes the answer.
  if (audioCapability === 'unavailable') {
    return { disabled: true, label: AUDIO_LABELS.noMic, tone: 'no-mic' };
  }

  // Advertised audio that never arrived. A delivery problem, not a hardware
  // one, and the wording has to keep those apart — "no microphone" here would
  // send someone to check the wrong end of the system.
  if (audioCapability === 'available' && isLive && graceExpired && !audioReceiving) {
    return { disabled: true, label: AUDIO_LABELS.noAudio, tone: 'no-audio' };
  }

  // 'available' before the grace expires, and 'unknown' always: enabled and
  // armed. Let the wire decide.
  return {
    disabled: false,
    label: muted ? AUDIO_LABELS.unmute : AUDIO_LABELS.mute,
    tone: muted ? 'muted' : 'unmuted'
  };
}

/**
 * `deriveAudioAffordance` plus the grace timer, which needs component state.
 *
 * The timer starts when the tile goes live and resets whenever it stops being
 * live or the channel changes, so an HD↔SD switch re-runs the whole question
 * rather than inheriting the previous channel's verdict.
 */
export function useAudioAffordance(mediaState, resetKey) {
  const {
    audioCapability = 'unknown',
    audioReceiving = false,
    audioUnavailableReason = null,
    muted = true,
    isLive = false,
    quality = 'sd'
  } = mediaState || {};

  const [graceExpired, setGraceExpired] = useState(false);
  const timerRef = useRef(null);

  // Quality is folded in here rather than left to callers. Both call sites pass
  // `${deviceSerial}_${channelId}` where channelId is the BASE camera id — it is
  // '2' on 201 and on 202 alike, so it does NOT change on an HD<->SD toggle,
  // while capability and delivery are per EFFECTIVE channel and do. The grace
  // timer happened to restart anyway, because the SDK tears the session down and
  // isLive drops, but that is an invariant of another package. Keying on quality
  // makes the reset a property of this hook, provable from this file.
  const graceKey = `${resetKey}_${quality}`;

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setGraceExpired(false);
    if (!isLive) return undefined;

    timerRef.current = setTimeout(() => {
      setGraceExpired(true);
      timerRef.current = null;
    }, AUDIO_NO_DATA_GRACE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isLive, graceKey]);

  return useMemo(
    () =>
      deriveAudioAffordance({
        audioCapability,
        audioReceiving,
        audioUnavailableReason,
        muted,
        isLive,
        graceExpired
      }),
    [audioCapability, audioReceiving, audioUnavailableReason, muted, isLive, graceExpired]
  );
}
